import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { ServiceProposalResponse, ServiceCategoryResponse } from '../types';

export default function ServiceProposalsPage() {
  const auth = useAuth();
  const [proposals, setProposals] = useState<ServiceProposalResponse[]>([]);
  const [categories, setCategories] = useState<ServiceCategoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Spec 069 — `?status=` preselects the filter (dashboard tile); absent keeps Pending.
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') ?? 'PENDING');

  // Approval/rejection state
  const [actionProposalId, setActionProposalId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveCategory, setApproveCategory] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!auth.token) return;
    setLoading(true);
    Promise.all([
      api.fetchProposals(statusFilter || undefined, auth.token),
      api.listCategories(false, auth.token),
    ])
      .then(([props, cats]) => {
        setProposals(props);
        setCategories(cats);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, statusFilter]);

  const handleApprove = async (proposalId: string) => {
    if (!auth.token) return;
    setProcessing(true);
    setError(null);
    try {
      await api.approveProposal(proposalId, {
        approvedCategoryId: approveCategory || undefined,
      }, auth.token);
      // Refresh
      const updated = await api.fetchProposals(statusFilter || undefined, auth.token);
      setProposals(updated);
      setActionProposalId(null);
      setActionType(null);
      setApproveCategory('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (proposalId: string) => {
    if (!auth.token || !rejectReason.trim()) return;
    setProcessing(true);
    setError(null);
    try {
      await api.rejectProposal(proposalId, rejectReason.trim(), auth.token);
      const updated = await api.fetchProposals(statusFilter || undefined, auth.token);
      setProposals(updated);
      setActionProposalId(null);
      setActionType(null);
      setRejectReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  // Flatten categories for dropdown
  const flatCategories: { id: string; name: string }[] = [];
  function walkCategories(cats: ServiceCategoryResponse[], prefix = '') {
    for (const cat of cats) {
      flatCategories.push({ id: cat.id, name: prefix ? `${prefix} > ${cat.name}` : cat.name });
      if (cat.children?.length) {
        walkCategories(cat.children, prefix ? `${prefix} > ${cat.name}` : cat.name);
      }
    }
  }
  walkCategories(categories);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading proposals...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-card-header" style={{ marginBottom: '1.5rem' }}>
        <h1 className="admin-card-title">Service Proposals</h1>
      </div>

      {error && (
        <div className="admin-info-banner" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="admin-toolbar" style={{ marginBottom: '1rem' }}>
        <select
          className="admin-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {proposals.length === 0 ? (
        <div className="admin-empty-state">
          <p>No proposals found{statusFilter ? ` with status "${statusFilter}"` : ''}.</p>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Proposed Name</th>
                  <th>Description</th>
                  <th>Suggested Category</th>
                  <th>Price</th>
                  <th>Duration</th>
                  <th>Garage</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th style={{ width: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 500 }}>{p.proposedName}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.description || '—'}
                    </td>
                    <td>{p.suggestedCategoryName || '—'}</td>
                    <td>{p.price != null ? `AED ${p.price.toFixed(2)}` : '—'}</td>
                    <td>{p.durationMinutes != null ? `${p.durationMinutes} min` : '—'}</td>
                    <td>{p.garageName || '—'}</td>
                    <td>
                      <span className={`admin-badge ${p.status === 'APPROVED' ? 'approved' : p.status === 'REJECTED' ? 'rejected' : 'pending'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                      {new Date(p.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      {p.status === 'PENDING' && (
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button
                            className="admin-btn admin-btn-sm"
                            style={{ background: 'var(--admin-success)', color: '#fff', border: 'none' }}
                            onClick={() => { setActionProposalId(p.id); setActionType('approve'); setApproveCategory(p.suggestedCategoryId || ''); }}
                          >
                            Approve
                          </button>
                          <button
                            className="admin-btn admin-btn-sm"
                            style={{ background: 'var(--admin-danger)', color: '#fff', border: 'none' }}
                            onClick={() => { setActionProposalId(p.id); setActionType('reject'); }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {p.status === 'REJECTED' && p.rejectionReason && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--admin-danger)' }}>
                          {p.rejectionReason}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {actionProposalId && actionType === 'approve' && (() => {
        const proposal = proposals.find(p => p.id === actionProposalId);
        return (
          <div className="admin-modal-overlay" onClick={() => { setActionProposalId(null); setActionType(null); }}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <h3>Approve Proposal</h3>
              </div>
              <div className="admin-modal-body">
                {proposal && (
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>{proposal.proposedName}</h4>
                    {proposal.description && <p style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', marginBottom: '0.5rem' }}>{proposal.description}</p>}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.8rem' }}>
                      <div><strong>Price:</strong> {proposal.price != null ? `AED ${proposal.price.toFixed(2)}` : '—'}</div>
                      <div><strong>Duration:</strong> {proposal.durationMinutes != null ? `${proposal.durationMinutes} min` : '—'}</div>
                      <div><strong>Garage:</strong> {proposal.garageName || '—'}</div>
                      <div><strong>Suggested Category:</strong> {proposal.suggestedCategoryName || '—'}</div>
                      {proposal.warrantyDescription && <div style={{ gridColumn: '1 / -1' }}><strong>Warranty:</strong> {proposal.warrantyDescription}</div>}
                      {proposal.carTypes && proposal.carTypes.length > 0 && (
                        <div style={{ gridColumn: '1 / -1' }}><strong>Car Types:</strong> {proposal.carTypes.map(ct => ct.name).join(', ')}</div>
                      )}
                    </div>
                    {proposal.media && proposal.media.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <strong style={{ fontSize: '0.8rem' }}>Photos:</strong>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                          {proposal.media.map(m => (
                            <img key={m.mediaId} src={m.downloadUrl} alt={m.originalFilename} style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                  Approving creates a new catalog entry (child of selected category) and an offering for this garage.
                </p>
                <div className="admin-form-group">
                  <label className="admin-form-label">Parent Category *</label>
                  <select
                    className="admin-select"
                    value={approveCategory}
                    onChange={(e) => setApproveCategory(e.target.value)}
                  >
                    <option value="">-- Select parent category --</option>
                    {flatCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  className="admin-btn admin-btn-outline"
                  onClick={() => { setActionProposalId(null); setActionType(null); }}
                >
                  Cancel
                </button>
                <button
                  className="admin-btn admin-btn-primary"
                  disabled={processing || !approveCategory}
                  onClick={() => handleApprove(actionProposalId)}
                >
                  {processing ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject Modal */}
      {actionProposalId && actionType === 'reject' && (
        <div className="admin-modal-overlay" onClick={() => { setActionProposalId(null); setActionType(null); }}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Reject Proposal</h3>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Rejection Reason *</label>
                <textarea
                  className="admin-textarea"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a reason for rejection..."
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-outline"
                onClick={() => { setActionProposalId(null); setActionType(null); setRejectReason(''); }}
              >
                Cancel
              </button>
              <button
                className="admin-btn"
                style={{ background: 'var(--admin-danger)', color: '#fff', border: 'none' }}
                disabled={!rejectReason.trim() || processing}
                onClick={() => handleReject(actionProposalId)}
              >
                {processing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
